import React, { useEffect, useState } from 'react';
import s from './PromptBlock.module.css';
import Button from '../../../../shared/ui/button/Button';
import IconExpand from '../../../../shared/ui/icons/IconExpand';
import { useSelector } from 'react-redux';
import {
    selectCurrentProject,
    selectIsTextSuggesting,
    selectScriptData,
    selectTextSuggestions,
} from '../../../../store/selectors/projectSelectors';
import { useAppDispatch } from '../../../../store/store';
import { generateTextSuggestion, updateProjectText } from '../../../../shared/api/textProjectApi';
import { setScriptData } from '../../../../store/slices/projectSlice';
import { ScriptData } from '../../../../shared/types/scriptTypes';
import { TextSuggestion } from '../../../../shared/types/projectTypes';

const SuggestionsBlock = () => {
    const currentProject = useSelector(selectCurrentProject);
    const scriptData = useSelector(selectScriptData);
    const suggestions = useSelector(selectTextSuggestions);
    const isTextSuggesting = useSelector(selectIsTextSuggesting);
    const dispatch = useAppDispatch();

    const [selectedSuggestions, setSelectedSuggestions] = useState<Set<number>>(new Set([1]));

    const displayedSuggestions: TextSuggestion[] = suggestions?.length
        ? suggestions
        : [
              { id: 1, content: '' },
              { id: 2, content: '' },
              { id: 3, content: '' },
          ];

    const handleSuggestionToggle = (id: number) => {
        const newSelected = new Set(selectedSuggestions);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedSuggestions(newSelected);
    };

    const handleRegenerate = async () => {
        if (!currentProject?.id) return;
        await dispatch(generateTextSuggestion({ projectId: currentProject.id }));
        setSelectedSuggestions(new Set([1]));
    };

    const handleApply = async () => {
        const selectedContents = displayedSuggestions
            .filter((s) => selectedSuggestions.has(s.id))
            .map((s) => s.content)
            .join('\n');

        if (!selectedContents || selectedContents.trim() === '' || !currentProject?.id) {
            return;
        }

        const newScriptData: ScriptData = {
            ...scriptData,
            body: selectedContents,
            title: scriptData?.title ?? '',
            preview: scriptData?.preview ?? '',
        };
        dispatch(setScriptData(newScriptData));
        await dispatch(updateProjectText({ projectId: currentProject.id, body: selectedContents }));
    };

    return (
        <div className={s.container}>
            <div className={s.block_container}>
                <div className={s.suggestions_header}>
                    <h3 className={s.bold_header}>New suggestions for your content</h3>
                    <p className={s.text}>Select which suggestion you would like to implement</p>
                </div>

                <div className={s.suggestions_list}>
                    {displayedSuggestions.map((suggestion) => (
                        <div key={suggestion.id} className={s.suggestion_card}>
                            <input
                                type="checkbox"
                                className={s.suggestion_checkbox}
                                checked={selectedSuggestions.has(suggestion.id)}
                                onChange={() => handleSuggestionToggle(suggestion.id)}
                            />
                            <div className={s.suggestion_content}>
                                <span>{suggestion.content}</span>
                            </div>
                            <div className={s.suggestion_expand}>
                                <IconExpand width={20} height={20} color="#666" />
                            </div>
                        </div>
                    ))}
                </div>

                <div className={s.suggestions_actions}>
                    <Button
                        text={isTextSuggesting ? 'Loading...' : 'Regenerate'}
                        onClick={handleRegenerate}
                        state={isTextSuggesting ? 'disabled' : 'enabled'}
                        style={{
                            backgroundColor: '#FFDD35',
                            color: '#000',
                            width: 'auto',
                            minWidth: '120px',
                        }}
                    />
                    <Button
                        text="Apply"
                        onClick={handleApply}
                        state={isTextSuggesting ? 'disabled' : 'enabled'}
                        style={{
                            backgroundColor: '#f5f5f5',
                            color: '#333',
                            border: '1px solid #ddd',
                            width: 'auto',
                            minWidth: '80px',
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default SuggestionsBlock;
